// frontend/src/components/ErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { trackClientException } from '../utils/appInsights';

type Props = {
  children: ReactNode;
  /** Optional compact recovery UI override */
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
  errorId: string | null;
};

function newErrorId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `err-${Date.now().toString(36)}`;
}

/**
 * Catches render / lifecycle crashes (white screens).
 * Surfaces a short error id for support; reports to App Insights when consent is on.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, errorId: null };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true, errorId: newErrorId() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const errorId = this.state.errorId || newErrorId();
    if (!this.state.errorId) {
      this.setState({ errorId });
    }
    trackClientException(error, {
      errorId,
      source: 'ErrorBoundary',
      componentStack: (info.componentStack || '').slice(0, 1500),
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }

  private handleReload = () => {
    window.location.assign('/');
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const id = this.state.errorId || 'unknown';

    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-dark-bg text-white px-6 text-center">
        <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
        <p className="text-gray-400 max-w-md mb-4">
          The page crashed unexpectedly. You can return home and try again. If it keeps happening,
          send this error id to support.
        </p>
        <p className="text-xs text-gray-500 font-mono mb-6 select-all">Error id: {id}</p>
        <button
          type="button"
          onClick={this.handleReload}
          className="px-5 py-2.5 rounded-xl bg-primary hover:bg-primary-hover font-semibold"
        >
          Back to home
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
